import { observable, computed, action, IObservableValue } from 'mobx';
import { ModalType } from '../modals/modal-type';
import { PayerOnboardingPage } from '../pages';
import * as Models from '../payer-onboarding-generated';
import { IPayerOnboardingPageViewModel } from '../pages/payer-onboarding-page-view-model';
import { PayerOnboardingUserAction } from '../payer-onboarding-user-actions';
import { Channel } from '../../Shared/utils/user-action-channel';
import { PageEnterDetailsViewModel } from '../pages/page-enter-details/page-enter-details-view-model';
import { createEmailVerificationNotification, NotificationViewModel } from '../../WebGiving/components/notification/notification';
import { PostError } from '../../Shared/utils/ajax-client';
import { PayerOnboardingViewModel } from '../payer-onboarding-generated';
import { IHistoryAdapter } from '../../WebGiving/utils/history-adapter';
import { defaultErrorMessage, getErrorInfo } from '../utils/error-utils';
import { PayerOnboardingErrorType } from '../components/error-message/payer-onboarding-error';

export interface IPayerOnboardingMainViewModel {
	readonly currentPageViewModel: IPayerOnboardingPageViewModel;
	readonly visibleModal: ModalType | null;
	readonly IsAnonymousSignUp: boolean;
	resendCodeDisabled: IObservableValue<boolean>;
	theme: Models.Theme;
	isAppLoading: boolean;
	notification: NotificationViewModel;

	readonly viewData: PayerOnboardingViewModel;

	readonly userActionChannel: Channel<PayerOnboardingUserAction>;
	setCurrentPageShouldBeVisible: () => void;

	openPage(viewModel: IPayerOnboardingPageViewModel): void;
	openModal(modalType: ModalType): void;
	closeModal(): void;
	showNotification(notification: NotificationViewModel): void;
	raiseAction(action: PayerOnboardingUserAction): void;
	reportError(error: any, customData?: any): void;
	showEmailNotification(emailAddress: string): void;
}

export class PayerOnboardingMainViewModel implements IPayerOnboardingMainViewModel {
	@computed
	get currentPageViewModel(): IPayerOnboardingPageViewModel {
		return this._currentPageViewModel;
	}

	@computed
	get visibleModal(): ModalType | null {
		return this._visibleModal;
	}

	get IsAnonymousSignUp(): boolean {
		return this.viewData.PersonInfo === null;
	}

	@observable
	isAccountDetailsOpen: boolean;

	@observable
	isAppLoading: boolean;

	@observable
	notification: NotificationViewModel;

	resendCodeDisabled = observable.box(false);

	@observable
	viewData: Models.PayerOnboardingViewModel;

	@observable
	theme: Models.Theme;

	@observable
	private _visibleModal: ModalType | null = null;

	@observable
	private _currentPageViewModel: IPayerOnboardingPageViewModel;

	constructor(
		viewData: Models.PayerOnboardingViewModel,
		public readonly userActionChannel: Channel<PayerOnboardingUserAction>,
		private readonly history: IHistoryAdapter,
		disableValidation: boolean = false) {

		this.viewData = viewData;
		this.theme = viewData.Theme;

		const personInfo = viewData.PersonInfo;
		const prefillInfo = viewData.PrefillInfo;
		const merchantInfo = viewData.MerchantInfo;

		const emailAddress = prefillInfo && prefillInfo.Email || '';
		const firstName = (personInfo && personInfo.FirstName) || (prefillInfo && prefillInfo.FirstName) || '';
		const lastName = (personInfo && personInfo.LastName) || (prefillInfo && prefillInfo.LastName) || '';
		const mobileNumber = (personInfo && personInfo.MobileNumber) || (prefillInfo && prefillInfo.MobileNumber) || '';

		const viewModel = new PageEnterDetailsViewModel(firstName, lastName, emailAddress, mobileNumber, merchantInfo.Country, this.IsAnonymousSignUp);
		this.openPage(viewModel);
		this.setCurrentPageShouldBeVisible();
	}

	@action.bound
	openPage(viewModel: IPayerOnboardingPageViewModel) {
		if (this._currentPageViewModel) {
			this.pushPageVisitToHistory(viewModel);
		}

		if (this.notification) {
			this.notification.dismiss();
		}

		this._currentPageViewModel = viewModel;
	}

	@action.bound
	openModal(modalType: ModalType) {
		this._visibleModal = modalType;
		this.history.push(false);
	}

	@action.bound
	closeModal() {
		this._visibleModal = null;
		this.pushPageVisitToHistory(this._currentPageViewModel);
	}

	@action.bound
	showNotification(notification: NotificationViewModel) {
		this.notification = notification;
	}

	setCurrentPageShouldBeVisible = () => {
		this.currentPageViewModel.setPageShouldBeVisible();
	}

	@action.bound
	raiseAction(action: PayerOnboardingUserAction) {
		this.userActionChannel.put(action);
	}

	@action.bound
	reportError(error: any, customData?: any) {
		const errorInfo = getErrorInfo(error);

		if (typeof errorInfo.resetPageReason === 'number') {
			this.currentPageViewModel.error = {
				errorType: PayerOnboardingErrorType.ShouldReloadPage,
				reason: errorInfo.resetPageReason,
			};
			this.currentPageViewModel.isBlocked = true;
		} else {
			this.currentPageViewModel.error = {
				errorType: PayerOnboardingErrorType.Generic,
				message: errorInfo.message || defaultErrorMessage,
			};
		}

		reportError(errorInfo.error, this.currentPageViewModel.type, customData);
	}

	@action.bound
	showEmailNotification(emailAddress: string) {
		const emailNotification = createEmailVerificationNotification({
			items: [
				{
					text: `Keep your account secure by verifying {{emailAddress}}, from the link we just emailed you.`,
					textParams: { emailAddress },
				},
			],
		});

		this.showNotification(emailNotification);
	}

	private pushPageVisitToHistory(viewModel: IPayerOnboardingPageViewModel) {
		const isFirstPage = viewModel.type === PayerOnboardingPage.EnterDetails;
		this.history.push(isFirstPage);
	}
}

function reportError(error: any, currentPage: PayerOnboardingPage, customData?: any) {
	if (error instanceof PostError) {
		if (error.client.status !== 500) {
			// let's ignore all the errors expect 500s because they are not unexpected
			return;
		}
	}

	window.reportUnhandledRejection(error, {
		...customData,
		currentPage: PayerOnboardingPage[currentPage],
	});
}
